import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Modal,
  Progress,
  Select,
  Typography,
  Upload,
  message,
} from "antd";
import { RcFile, UploadFile } from "antd/es/upload";
import axios from "axios";
import React, { useState } from "react";
import { CreateFileDto, useCreateFile } from "~/services/file";
import { useGetPresignedUrl } from "~/services/upload";
import { useAuth } from "~/store";
import { DocumentStatus, DocumentType } from "~/utils/constants";

const { Text } = Typography;
const { Option } = Select;

interface DocumentFile extends RcFile {
  url?: string;
  preview?: string;
  originFileObj?: RcFile;
}

interface ProfileDocumentUploadProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface DocumentFormValues {
  type: DocumentType;
}

export const ProfileDocumentUpload: React.FC<ProfileDocumentUploadProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<DocumentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const getPresignedUrlMutation = useGetPresignedUrl();
  const createFileMutation = useCreateFile();
  const { user } = useAuth();

  const handleUploadChange = ({ fileList }: any) => {
    // Validate file types
    const validTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/csv",
    ];

    const filteredFiles = fileList.filter((file: any) => {
      const isValidType = validTypes.includes(file.type);
      if (!isValidType && file.status !== "removed") {
        message.error(
          `File ${file.name} is not a valid document type (PDF, DOCX, JPG, PNG, CSV)`
        );
      }
      return isValidType;
    });

    setFileList(filteredFiles);
  };

  const getFileTitle = (fileName: string): string => {
    // Remove extension and format as title
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
    return nameWithoutExt
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleSubmit = async (values: DocumentFormValues) => {
    if (fileList.length === 0) {
      message.error("Please select at least one document to upload");
      return;
    }

    if (!user?.client?.id) {
      message.error("Client information not found");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setCurrentFileIndex(0);

    const totalFiles = fileList.length;
    let successCount = 0;
    let failCount = 0;

    try {
      for (let i = 0; i < fileList.length; i++) {
        const fileItem = fileList[i];
        const file = fileItem.originFileObj || fileItem;

        if (!file) {
          failCount++;
          continue;
        }

        setCurrentFileIndex(i + 1);

        try {
          // Step 1: Get presigned URL from server
          const presignedUrlData = await getPresignedUrlMutation.mutateAsync({
            fileName: file.name,
            mimeType: file.type,
          });

          // Step 2: Upload file directly to storage using presigned URL
          await axios.put(presignedUrlData.presignedUrl, file, {
            headers: {
              "Content-Type": file.type,
            },
          });

          // Step 3: Create file record in database with the uploaded file URL
          // Automatically tag with clientId and no jobTicketId (profile documents)
          const fileData: CreateFileDto = {
            title: getFileTitle(file.name),
            type: values.type,
            status: DocumentStatus.SENT,
            filePath: presignedUrlData.url,
            clientId: user.client.id, // Always tag with client ID
            // No jobTicketId - these are profile documents
          };

          await createFileMutation.mutateAsync(fileData);
          successCount++;
        } catch (err: any) {
          console.error(`Error uploading ${file.name}:`, err);
          failCount++;
          message.error(`Failed to upload ${file.name}`);
        }

        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }

      if (successCount > 0) {
        message.success(
          `Successfully uploaded ${successCount} document${successCount > 1 ? "s" : ""}`
        );
        if (failCount > 0) {
          message.warning(`${failCount} document${failCount > 1 ? "s" : ""} failed to upload`);
        }
        setFileList([]);
        form.resetFields();
        onSuccess();
        onClose();
      } else {
        message.error("Failed to upload all documents");
      }
    } catch (error) {
      console.error("Error uploading documents:", error);
      message.error("Failed to upload documents. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentFileIndex(0);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Upload Profile Documents"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: DocumentType.OTHER,
        }}
      >
        <Form.Item
          name="type"
          label="Document Type"
          rules={[{ required: true, message: "Please select document type" }]}
        >
          <Select placeholder="Select document type">
            <Option value={DocumentType.CONTRACT}>Contract</Option>
            <Option value={DocumentType.LEGAL}>Legal</Option>
            <Option value={DocumentType.ADMINISTRATIVE}>Administrative</Option>
            <Option value={DocumentType.INVENTORY}>Inventory</Option>
            <Option value={DocumentType.SUPPLEMENTARY}>Supplementary</Option>
            <Option value={DocumentType.OTHER}>Other</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Document Files"
          rules={[{ required: true, message: "Please upload at least one document file" }]}
        >
          <Upload
            beforeUpload={() => false}
            onChange={handleUploadChange}
            fileList={fileList as UploadFile[]}
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.csv"
          >
            <Button icon={<UploadOutlined />}>Select Files</Button>
          </Upload>
          <Text type="secondary" className="mt-2 block">
            Supported formats: PDF, DOCX, JPG, PNG, CSV (Max: 10MB per file)
            <br />
            You can select multiple files at once
          </Text>
        </Form.Item>

        {isUploading && (
          <Form.Item>
            <div className="space-y-2">
              <Text type="secondary">
                Uploading {currentFileIndex} of {fileList.length} files...
              </Text>
              <Progress percent={uploadProgress} status="active" />
            </div>
          </Form.Item>
        )}

        <Form.Item>
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isUploading}
              disabled={fileList.length === 0}
            >
              {fileList.length > 1 ? `Upload ${fileList.length} Documents` : "Upload Document"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

